/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useMemo, createContext, useContext } from 'react';
import dynamic from 'next/dynamic';
import { EditableComponent, ResponsiveGrid } from '@adobe/aem-react-editable-components';
import { Button, eventUtil, Icon } from '@marriott/mi-ui-library';
import clsx from 'clsx';
import {
  searchRestaurantsByGeolocation,
  searchRestaurantsByLocation,
  searchRestaurantsByOpenText,
} from '@marriott/mi-awp-graphql';
import { useApolloClient } from '@apollo/client';
import Cookies from 'js-cookie';
import {
  PageSize,
  outletPromotionCurrPage,
  AWPFILTERS,
  otsUrlTypeTerm,
  otsUrlTypeValue,
  TRACKING_CONSTANT,
  fullbleedClass,
  isOpenNow,
  defaultENUS,
  TARGET_SEGMENT_API_ENDPOINT,
  targetMapControl,
} from '../../modules/utils/constants';

// organisms
import { SkeletonOutletCardLoader } from './SkeletonOutletCardLoader';
import { PromotionBanner } from './PromotionBanner';

// molecules
import { PaginationPanel } from '../../molecules/Pagination';

// Global Styles
import {
  StyledSearchResultContainer,
  GlobalStyle,
  GlobalUnsetMinHeight,
  GlobalScrollIOS,
} from './SearchResults.styles';

// Helpers
import {
  useGetBreakpoint,
  getHttpStatusCode,
  getErrorMessage,
  getCategorizedBrands,
  getAEMKeyFromProps,
  getProcessedFilterData,
  getSubDirectoryLocale,
  updateDistanceFilterUnits,
  getGoogleCurrentLocation,
  getAWPTntId,
  getDirection,
} from '../../modules/utils/helpers';

// Store
import { awpErrorState, awpErrorStore } from '../../modules/store/awpErrorStore';
import { searchResultsState, useStore } from '../../modules/store/searchResultsStore';
import { autoCompleteState, useAutoCompleteStore } from '../../modules/store/autoCompleteDetailsStore';
import searchFilterStore, { searchFilterState } from '../../modules/store/store';

// Mocks
import searchResultsMockData from './__mocks__/SearchResultsFiltersMock.json';
import { PageParamsContext } from '../../modules/context';
import { GoogleMapComponent } from './GoogleMapComponent/index';
import { StyledErrorMessage } from '../../molecules/AwpErrorMessage/AwpErrorMessage.styles';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import { targetPayloadRequest } from '@marriott/mi-headless-utils';
import Axios from 'axios';

// Dynamic Imports
const SearchAllFilters = dynamic<unknown>(() => import('./SearchAllFilters').then(mod => mod.SearchAllFilters));

const SearchOutletCard = dynamic<unknown>(() => import('./SearchOutletCard').then(mod => mod.SearchOutletCard));

const { NEXT_PUBLIC_AEM_SITE } = process.env;

export const SearchResults = (props: any) => {
  const { itemPath, isAuthorMode, pagePath, totalNumberOfCards, cqItems, cardCount } = props;
  const [currentPage, setCurrentPage] = useState(1);
  const paginationChange = useStore((state: searchResultsState) => state.paginationChange);
  const pageNumber = useStore((state: searchResultsState) => state.pageNumber);
  const [startIndex, setStartIndex] = useState(0);
  const [endIndex, setEndIndex] = useState(0);
  const searchResultsData = useStore((state: searchResultsState) => state.searchResultData);
  let filterUpdateData = useStore((state: searchResultsState) => state.facetsData);
  const searchResultsDataError = useStore((state: searchResultsState) => state.searchResultDataError);
  const setData = useStore((state: searchResultsState) => state.setData);
  const setPaginationChange = useStore((state: searchResultsState) => state.setPaginationChange);
  const setPageNumber = useStore((state: searchResultsState) => state.setPageNumber);
  const selectedPlaceDetailsData = useAutoCompleteStore((state: autoCompleteState) => state.selectedPlaceDetailsData);
  const searchResultDataLoading = useStore((state: searchResultsState) => state.searchResultDataLoading);
  const TotalPages = Math.ceil(searchResultsData?.total / PageSize);
  const promBannerIndex = Math.ceil(PageSize / 2);
  const setErrorState = awpErrorStore((state: awpErrorState) => state.setErrorState);
  const errorMessage = awpErrorStore((state: awpErrorState) => state.errorMessage);
  const [errorCheck, setErrorCheck] = useState(false);
  const [isMobileViewPort, setIsMobileViewPort] = useState(useGetBreakpoint() === 'mobile');
  const setIsComponentLoading = useStore((state: searchResultsState) => state.setIsComponentLoading);
  const isComponentLoading = useStore((state: searchResultsState) => state.isComponentLoading);
  const queryParam = useStore((state: searchResultsState) => state.queryParam);
  const setQueryParam = useStore((state: searchResultsState) => state.setQueryParam);
  const setIsLoaderVisible = useStore((state: searchResultsState) => state.setIsLoaderVisible);
  const shouldComponentReload = useStore((state: searchResultsState) => state.shouldComponentReload);
  const sortMessage = useStore((state: searchResultsState) => state.sortMessage);
  const searchTextMessage = useStore((state: searchResultsState) => state.searchTextMessage);
  const setSearchTypeMessage = useStore((state: searchResultsState) => state.setSearchTypeMessage);
  const searchTextMessageDisplay = useStore((state: searchResultsState) => state.searchTextMessageDisplay);
  const {
    campaignParams,
    currentLocale,
    currentPage: currentPageFromContext,
    awperror,
    userId,
  } = useContext(PageParamsContext);
  const subLocale = getSubDirectoryLocale(currentLocale || 'en-US');
  const setIsMapViewEnabled = useStore((state: searchResultsState) => state.setIsMapViewEnabled);
  const [isMapView, setIsMapView] = useState(false);
  const [isMapOnFullScreen, setIsMapOnFullScreen] = useState(false);
  const setLat = useStore((state: searchResultsState) => state.setLat);
  const setLong = useStore((state: searchResultsState) => state.setLong);

  const pageContext = useContext(createContext<any>({}));

  const requestId = useMemo(() => {
    return pageContext.requestId;
  }, [pageContext]);

  const sessionID = Cookies.get('sessionID');
  let latitude: string | number | null = null;
  let longitude: string | number | null = null;
  let state: string | null = null;
  let country: string | null = null;
  let isOpenTextSearch: boolean | null = null;
  let userInputOpenSearchTerm: string | null = null;
  const terms: any = [];
  const ranges: any = [];
  const hours = searchFilterStore((state: searchFilterState) => state.hours);
  const cuisine = searchFilterStore((state: searchFilterState) => state.cuisine);
  const seating = searchFilterStore((state: searchFilterState) => state.seating);
  const bonvoyMembers = searchFilterStore((state: searchFilterState) => state.bonvoyMembers);
  const brands = searchFilterStore((state: searchFilterState) => state.brands);
  const venueType = searchFilterStore((state: searchFilterState) => state.venueType);
  const dietaryOptions = searchFilterStore((state: searchFilterState) => state.dietaryOptions);
  const experiences = searchFilterStore((state: searchFilterState) => state.experiences);
  const city = searchFilterStore((state: searchFilterState) => state.city);
  const stateProvinceDescription = searchFilterStore((state: searchFilterState) => state.stateProvinceDescription);
  const countryDescription = searchFilterStore((state: searchFilterState) => state.countryDescription);
  const openNow = searchFilterStore((state: searchFilterState) => state.openNow);
  const price = searchFilterStore((state: searchFilterState) => state.price);
  const hourSelection = searchFilterStore((state: searchFilterState) => state.hourSelection);
  const cuisineSelection = searchFilterStore((state: searchFilterState) => state.cuisineSelection);
  const distanceSelection = searchFilterStore((state: searchFilterState) => state.distanceSelection);
  const seatingSelection = searchFilterStore((state: searchFilterState) => state.seatingSelection);
  const bonvoySelection = searchFilterStore((state: searchFilterState) => state.bonvoySelection);
  const brandSelection = searchFilterStore((state: searchFilterState) => state.brandSelection);
  const venueTypeSelection = searchFilterStore((state: searchFilterState) => state.venueTypeSelection);
  const dietaryOptionsSelection = searchFilterStore((state: searchFilterState) => state.dietaryOptionsSelection);
  const experiencesSelection = searchFilterStore((state: searchFilterState) => state.experiencesSelection);
  const citySelection = searchFilterStore((state: searchFilterState) => state.citySelection);
  const stateProvinceDescSelection = searchFilterStore((state: searchFilterState) => state.stateProvinceDescSelection);
  const countryDescSelection = searchFilterStore((state: searchFilterState) => state.countryDescSelection);
  const openNowSelection = searchFilterStore((state: searchFilterState) => state.openNowSelection);
  const priceSelection = searchFilterStore((state: searchFilterState) => state.priceSelection);
  const [ctaLabel, setCtaLabel] = useState<string>(props?.listLabel);
  const [searchAreaLat, setSearchAreaLat] = useState(0);
  const [searchAreaLong, setSearchAreaLong] = useState(0);
  const setIsCurrentLocation = useAutoCompleteStore((state: autoCompleteState) => state.setIsCurrentLocation);
  const [isTargetCallCompleted, setIsTargetCallCompleted] = useState(currentLocale === defaultENUS ? false : true);
  const [offsetHeight, setOffsetHeight] = useState(0);
  const [windowHeight, setWindowHeight] = useState(0);
  const [segmentIdValue, setSegmentIdValue] = useState('');
  const isIOSDevice =
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.userAgent.includes('Macintosh') && 'ontouchend' in document);
  const isDirectionRTL = getDirection() === 'rtl';

  const { SEARCH_RESULT_PAGE, MAP_CURRENT_LOCATION, MAP_VIEW, INTERNAL_LINK } = TRACKING_CONSTANT;

  const onPageItemClick = (page: number): void => {
    setPaginationChange(true);
    setCurrentPage(page);
    setPageNumber(page);
    calculateIndex(page);
    window.scrollTo(0, 0);
  };

  const replacePlaceholderText = (requestedTerm: string, correctedTerm: string) => {
    const placeholders = searchTextMessage?.match(/\[.+?\]/g) || [];
    let updatedText = searchTextMessage;

    if (placeholders && placeholders[0]) {
      updatedText = updatedText?.replace(placeholders[0], requestedTerm);
    }
    if (placeholders && placeholders[1]) {
      updatedText = updatedText?.replace(placeholders[1], correctedTerm);
    }
    setSearchTypeMessage(sortMessage, searchTextMessage, placeholders ? updatedText : '');
  };

  useEffect(() => {
    setQueryParam(window.location.search);
    if (currentLocale === defaultENUS && props?.enableTarget && props?.mapLabel) callSegmentApi();
    else setIsTargetCallCompleted(true);
  }, []);

  useEffect(() => {
    if (props?.isAuthorMode) {
      setData(searchResultsMockData?.data, false, false, props?.dynamicMedia);
    }
  }, [props?.isAuthorMode]);

  useEffect(() => {
    setPayloadDetails();
    setFacetsPayload();
  }, [queryParam, pageNumber, shouldComponentReload]);

  useEffect(() => {
    setIsComponentLoading(false);
  }, []);

  // useEffect(() => {
  //   const handlePopstate = () => {
  //     // Reload the page when the user hits the back button
  //     window.location.reload();
  //   };

  //   // Attach the event listener when the component mounts
  //   window.addEventListener('popstate', handlePopstate);

  //   // Clean up the event listener when the component unmounts
  //   return () => {
  //     window.removeEventListener('popstate', handlePopstate);
  //   };
  // }, []);

  useEffect(() => {
    if (!paginationChange) {
      setCurrentPage(1);
      setPageNumber(1);
      calculateIndex(1);
    } else {
      calculateIndex(pageNumber);
    }
    if (searchResultsData && searchResultsData?.edges?.length < 1) {
      if (isOpenTextSearch) {
        setErrorState(0, 'openTextError');
      } else {
        window.location.search.includes(AWPFILTERS.distance)
          ? setErrorState(0, 'mileGenericError')
          : setErrorState(0, 'mileError');
      }
      setData([], false, false, []);
      setErrorCheck(true);
    } else if (
      searchResultsData &&
      searchResultsData?.edges?.length > 1 &&
      searchResultsData?.status?.[0]?.['__typename'] !== 'RestaurantsWithExpandedSearchRadiusInfo'
    ) {
      setErrorCheck(false);
      setErrorState(0, '');
    }
    if (
      searchResultsData &&
      searchResultsData?.edges?.length > 1 &&
      searchResultsData?.status?.[0]?.['code'] === 'SPELL_CORRECTED_INFO'
    ) {
      replacePlaceholderText(
        searchResultsData?.status?.[0]?.requestedSearchTerm,
        searchResultsData?.status?.[0]?.correctedSearchTerm
      );
    }
  }, [searchResultsData, shouldComponentReload, pageNumber]);

  useEffect(() => {
    /**
     * Getting current Data and Time to generate unique Correlation Id
     */
    if (!props?.isAuthorMode) {
      const currentTimestamp = Date.now();
      const currentDateTimeStamp = new Date(currentTimestamp).getTime();
      const query = new URLSearchParams(queryParam);
      const isByLocationSearch = {
        country: country,
        state: state,
        options: {
          showOpenNow: query?.get(isOpenNow) === 'true' ? true : false,
        },
        facets: { terms: terms, ranges: ranges },
      };
      const isByGeoLocationSearch = {
        latitude: latitude,
        longitude: longitude,
        options: {
          showOpenNow: query?.get(isOpenNow) === 'true' ? true : false,
        },
        facets: { terms: terms, ranges: ranges },
      };
      const isByCountrySearch = {
        country: country,
        options: {
          showOpenNow: query?.get(isOpenNow) === 'true' ? true : false,
        },
        facets: { terms: terms, ranges: ranges },
      };

      const isByOpenTextSearch = {
        query: userInputOpenSearchTerm,
        facets: { terms: terms, ranges: ranges },
      };

      const locationQueryType =
        latitude && longitude
          ? searchRestaurantsByGeolocation
          : state && country
          ? searchRestaurantsByLocation
          : isOpenTextSearch
          ? searchRestaurantsByOpenText
          : searchRestaurantsByLocation;

      if ((latitude && longitude) || (state && country) || country || userInputOpenSearchTerm) {
        getSearchResultsDetails(
          {
            variables: {
              search:
                latitude && longitude
                  ? isByGeoLocationSearch
                  : state && country
                  ? isByLocationSearch
                  : isOpenTextSearch
                  ? isByOpenTextSearch
                  : isByCountrySearch,
              limit: PageSize,
              offset: pageNumber === 1 ? 0 : (pageNumber - 1) * PageSize,
            },
            context: {
              headers: {
                // Any header, including x-request-id, can be passed ui-libraryin args with query.
                // If you don't pass it in the authLink will generate a random ID.

                'accept-language': currentLocale?.replace('_', '-') ?? 'en-US',
                'x-request-id': requestId,
                'x-b3-traceId': `${sessionID ?? sessionID ?? 'fallback-token'} - ${currentDateTimeStamp}`,
                'x-b3-spanId': requestId !== '' ? requestId : `${currentDateTimeStamp}`,
                'correlation-id': `${sessionID ?? sessionID ?? 'fallback-token'} - ${currentDateTimeStamp}`,
              },
            },
          },
          locationQueryType
        );
      }
    }
  }, [
    latitude,
    longitude,
    terms,
    ranges,
    pageNumber,
    state,
    country,
    shouldComponentReload,
    userInputOpenSearchTerm,
    openNow,
  ]);

  const applyHeight = () => {
    const header = document.getElementsByClassName('m-header')[0] ?? document.getElementsByClassName('banner-row')[0];
    const searchForm = document.getElementsByClassName('awp-search-form')[0];
    const pointsHeader = document.getElementsByClassName('m-header__points')[0];

    if (!header && !searchForm) return;

    setWindowHeight(window?.innerHeight);
    setOffsetHeight(
      header?.getBoundingClientRect()?.height +
        searchForm?.getBoundingClientRect()?.height +
        (window?.innerWidth <= 991
          ? pointsHeader?.getBoundingClientRect()?.height > 0
            ? pointsHeader?.getBoundingClientRect()?.height
            : userId
            ? 48
            : 0
          : 0 || 0) +
        (terms?.length > 0 || ranges?.length > 0 ? 71 : 68)
    );
  };

  useEffect(() => {
    function handleResize() {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      setIsMobileViewPort(useGetBreakpoint() === 'mobile');
    }

    window.addEventListener('resize', applyHeight);
    window.addEventListener('resize', handleResize);

    eventUtil.on('updateCtaLabel', data => {
      setCtaLabel(props?.searchThisAreaLabel);
      setSearchAreaLat(data[0]?.lat);
      setSearchAreaLong(data[0]?.lng);
    });

    eventUtil.on('resetCtaLabel', _data => {
      setCtaLabel(props?.listLabel);
    });

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('resize', applyHeight);
    };
  }, []);

  useEffect(() => applyHeight(), [isMapView]);

  useEffect(() => {
    if (currentLocale === defaultENUS && !props?.enableTarget) {
      const isMapView = sessionStorage.getItem('awpMapViewEnabled') === 'true';
      const isMapFullScreen = sessionStorage.getItem('awpMapFullScreen') === 'true';
      setIsMapView(isMapView);
      setIsMapViewEnabled(isMapView);
      setIsMapOnFullScreen(isMapFullScreen);
    } else {
      sessionStorage.setItem('awpMapViewEnabled', 'false');
      sessionStorage.setItem('awpMapFullScreen', 'false');
      setIsMapView(false);
      setIsMapViewEnabled(false);
      setIsMapOnFullScreen(false);
    }
  }, []);

  const setPayloadDetails = () => {
    const query = new URLSearchParams(queryParam);
    isOpenTextSearch = query.has(otsUrlTypeTerm) && query?.get(otsUrlTypeTerm) === otsUrlTypeValue;

    if (props?.filtersOrderList?.some((filter: any) => filter?.filterName === AWPFILTERS.openNow)) {
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set(isOpenNow, String(openNow));
      const queryString = searchParams.toString();
      window.history.replaceState({}, '', `?${queryString}`);
    }
    if (selectedPlaceDetailsData) {
      latitude = selectedPlaceDetailsData?.selectedPlaceDetails?.data?.suggestedPlaceDetails?.location?.latitude;
      longitude = selectedPlaceDetailsData?.selectedPlaceDetails?.data?.suggestedPlaceDetails?.location?.longitude;
      state = selectedPlaceDetailsData?.selectedPlaceDetails?.data?.suggestedPlaceDetails?.location?.state;
      country = selectedPlaceDetailsData?.selectedPlaceDetails?.data?.suggestedPlaceDetails?.location?.country;
    } else if (!selectedPlaceDetailsData && query.get('lat') && query.get('long')) {
      const lat: string | number = parseFloat(query?.get('lat')?.toString() ?? '0');
      const long: string | number = parseFloat(query?.get('long')?.toString() ?? '0');
      latitude = lat;
      longitude = long;
    } else if (
      !selectedPlaceDetailsData &&
      !query.get('lat') &&
      !query.get('long') &&
      query.get('term2') &&
      query.get('term3')
    ) {
      state = query?.get('term2');
      country = query?.get('term3');
    } else if (
      !selectedPlaceDetailsData &&
      !query.get('lat') &&
      !query.get('long') &&
      !query.get('term2') &&
      query.get('term3')
    ) {
      country = query?.get('term3');
    } else if (isOpenTextSearch) {
      userInputOpenSearchTerm = query?.get('term');
    }
  };

  const setFacetsPayload = () => {
    const query = new URLSearchParams(queryParam);
    let facetsVal: { type: string; dimensions: string[] } = {
      type: '',
      dimensions: [],
    };
    const routeKeys: string[] = [];
    query.forEach((_value, key) => {
      routeKeys.push(key);
    });
    const filteredArray = routeKeys.filter((value: string) => {
      return props?.filtersOrderList?.some((obj: any) => obj?.filterName === value);
    });

    hourSelection([]);
    cuisineSelection([]);
    distanceSelection([]);
    seatingSelection([]);
    bonvoySelection([]);
    brandSelection([]);
    venueTypeSelection([]);
    dietaryOptionsSelection([]);
    experiencesSelection([]);
    citySelection([]);
    stateProvinceDescSelection([]);
    countryDescSelection([]);
    priceSelection([]);
    openNowSelection(query?.get(isOpenNow) === 'true' ? true : false);

    if (filteredArray.length > 0) {
      filteredArray.forEach((filter: string) => {
        if (filter === 'brandId') {
          facetsVal = { type: '', dimensions: [] };
          const values: any = query?.get(filter) ? query?.get(filter) : '';
          let selectedVal: any = [];
          selectedVal = values?.split(',');
          facetsVal.type = filter;
          if (brands?.length > 0) {
            const getSelectedBrandObj = brands.filter((element: any) => selectedVal.includes(element.brandTagId));
            const brandTagIds = getSelectedBrandObj?.map((brand: any) => brand.brandTagId);
            facetsVal['dimensions'] = brandTagIds;
          } else facetsVal['dimensions'] = [...selectedVal];
          terms.push(facetsVal);
        } else if (filter === 'distance') {
          const distanceFacetsVal: {
            type: string;
            endpoints: string[];
            dimensions: string[];
          } = { type: '', endpoints: [], dimensions: ['1'] };
          const values: any = query?.get(filter) ? query?.get(filter) : '';
          distanceFacetsVal.type = filter;
          distanceFacetsVal['endpoints'] = [values.split(' ')[0], values.split(' ')[2]];
          ranges.push(distanceFacetsVal);
          storeUpdater(filter);
        } else {
          storeUpdater(filter);
        }
      });
    }
  };

  const generateQueryParams = (filterData: any, selectedVal: any, facetsVal: any) => {
    if (filterData?.length > 0) {
      const getSelectedFilter = filterData.filter((element: any) => selectedVal.includes(element.label));
      const filterLabels = getSelectedFilter?.map((filterName: any) => filterName.label);
      if (facetsVal.type === 'hours' || facetsVal.type === 'bonvoyMembers') {
        facetsVal['dimensions'] = getAEMKeyFromProps(props, filterLabels);
      } else if (facetsVal.type === 'city') {
        facetsVal['dimensions'] = filterLabels?.map((dimension: any) => {
          if (dimension?.includes(' -')) {
            const updatedDimension = dimension?.replace(' -', ',');
            return updatedDimension;
          } else return dimension;
        });
      } else facetsVal['dimensions'] = filterLabels;
    } else {
      if (facetsVal.type === 'hours' || facetsVal.type === 'bonvoyMembers') {
        facetsVal['dimensions'] = getAEMKeyFromProps(props, selectedVal);
      } else if (facetsVal.type === 'city') {
        facetsVal['dimensions'] = selectedVal?.map((dimension: any) => {
          if (dimension?.includes(' -')) {
            const updatedDimension = dimension?.replace(' -', ',');
            return updatedDimension;
          } else return dimension;
        });
      } else facetsVal['dimensions'] = [...selectedVal];
    }
    terms.push(facetsVal);
  };

  const storeUpdater = (filter: string) => {
    const query = new URLSearchParams(window.location.search);
    const facetsVal = { type: '', dimensions: [] };
    const values: any = query?.get(filter) ? query?.get(filter) : '';
    let selectedVal: string[] = [];
    selectedVal = values?.split(',');
    facetsVal.type = filter;
    switch (facetsVal.type) {
      case 'hours':
        generateQueryParams(hours, selectedVal, facetsVal);
        break;

      case 'cuisine':
        generateQueryParams(cuisine, selectedVal, facetsVal);
        break;

      case 'seating':
        generateQueryParams(seating, selectedVal, facetsVal);
        break;

      case 'bonvoyMembers':
        generateQueryParams(bonvoyMembers, selectedVal, facetsVal);
        break;

      case 'brandId':
        brandSelection(facetsVal);
        break;

      case 'distance': {
        const updatedDistanceFilterOptions = updateDistanceFilterUnits(currentLocale ? currentLocale : 'en-US');
        const selectedDistance = updatedDistanceFilterOptions.filter(
          (distance: any) => distance.label === selectedVal[0]
        );
        distanceSelection(selectedDistance);
        break;
      }

      case 'venueType':
        generateQueryParams(venueType, selectedVal, facetsVal);
        break;

      case 'dietaryOptions':
        generateQueryParams(dietaryOptions, selectedVal, facetsVal);
        break;

      case 'experiences':
        generateQueryParams(experiences, selectedVal, facetsVal);
        break;

      case 'city':
        generateQueryParams(city, selectedVal, facetsVal);
        break;

      case 'stateProvinceDescription':
        generateQueryParams(stateProvinceDescription, selectedVal, facetsVal);
        break;

      case 'countryDescription':
        generateQueryParams(countryDescription, selectedVal, facetsVal);
        break;

      case 'price': {
        generateQueryParams(price, selectedVal, facetsVal);
        break;
      }
    }
  };

  const calculateIndex = (page: number) => {
    const startInd = (page - 1) * PageSize;
    const endInd = startInd + PageSize;
    setStartIndex(startInd);
    setEndIndex(endInd);
    if (page === TotalPages) {
      setEndIndex(searchResultsData?.total);
    }
  };

  const updateStoreData = (name: string, filterName: string, storeFilterUpdater: any, query: URLSearchParams) => {
    const filterData = getProcessedFilterData(
      filterUpdateData?.facets,
      name,
      props,
      currentLocale ? currentLocale : 'en-US'
    );
    const filterValues: any = query.get(filterName) ? query.get(filterName) : '';
    if (filterValues) {
      let selectedFilter: any = [];
      selectedFilter = filterValues.split(',');
      const selectedFilterArr: any = [];
      filterData.forEach((item: any) => {
        if (selectedFilter.includes(item.label)) selectedFilterArr.push(item);
      });
      storeFilterUpdater(selectedFilterArr);
    }
  };

  const enableMapView = () => {
    // Ensure viewport and offsets are synced before toggling to map view
    applyHeight();
    // Scroll to top to avoid visual viewport URL-bar transitions leaving gaps
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    setIsMapView(true);
    setIsMapViewEnabled(true);
    sessionStorage.setItem('awpMapViewEnabled', 'true');
    eventUtil.dispatch('resetCtaLabel', []);
  };

  const enableListView = () => {
    setIsMapView(false);
    setIsMapViewEnabled(false);
    sessionStorage.setItem('awpMapViewEnabled', 'false');
  };

  const searchThisArea = () => {
    if (window.google) {
      const geocoder = new window.google.maps.Geocoder();
      const latlng = { lat: searchAreaLat, lng: searchAreaLong };

      // using Goggle Maps API reverse geocoding service to convert latlng to location details
      geocoder.geocode({ location: latlng }, (results: any, status: any) => {
        if (status === 'OK') {
          if (results[0]) {
            const currentLocationInfo = {
              description: results[0].formatted_address,
              placeId: results[0].place_id,
              primaryDescription: '',
              secondaryDescription: '',
              category: '',
            };
            eventUtil.dispatch('loadCurrentLocationResults', currentLocationInfo);
          }
          // TODO: Handle error logic here
        }
        eventUtil.dispatch('resetCtaLabel', []);
      });
    }
  };
  const enableCurrentLocationSearch = () => {
    const currentLocationButton = document.getElementById('current-location-button');
    currentLocationButton?.classList.remove('m-button-gallery');
    currentLocationButton?.classList.add('m-button-s');
    currentLocationButton?.classList.add('m-button-primary-icon');

    const locationHandler = (data: Record<string, string>): void => {
      if (data) {
        setIsCurrentLocation(false);
        const currentLocationInfo = {
          description: data['address'],
          placeId: data['placeId'] || '',
          primaryDescription: '',
          secondaryDescription: '',
          category: '',
        };
        eventUtil.dispatch('loadCurrentLocationResults', currentLocationInfo);
        eventUtil.dispatch('resetCtaLabel', []);
      } else {
        setIsCurrentLocation(true);
        eventUtil.dispatch('resetCtaLabel', []);
      }
      eventUtil.dispatch('resetCtaLabel', []);
      currentLocationButton?.classList.remove('m-button-s');
      currentLocationButton?.classList.remove('m-button-primary-icon');
      currentLocationButton?.classList.add('m-button-gallery');
    };
    getGoogleCurrentLocation(locationHandler);
  };

  const toggleSideSearchResults = () => {
    if (isMapOnFullScreen) {
      setIsMapOnFullScreen(false);
      sessionStorage.setItem('awpMapFullScreen', 'false');
    } else {
      setIsMapOnFullScreen(true);
      sessionStorage.setItem('awpMapFullScreen', 'true');
    }
  };

  // UXL call to get Search Results
  const searchResultDetailsClient = useApolloClient();
  async function getSearchResultsDetails(inputValues: any, locationQueryType: any) {
    setLat(inputValues?.variables?.search?.latitude || 0);
    setLong(inputValues?.variables?.search?.longitude || 0);
    setData([], true, false, []);
    let searchResults,
      searchResultsError = false,
      insufficentDataError = false;
    try {
      const { data } = await searchResultDetailsClient.query({
        query: locationQueryType,
        errorPolicy: 'all',
        ...inputValues,
      });
      if (
        data &&
        (data?.searchRestaurantsByGeolocation || data?.searchRestaurantsByLocation || data?.searchRestaurantsByOpenText)
      ) {
        let validData;
        if (locationQueryType === searchRestaurantsByGeolocation)
          validData = data?.searchRestaurantsByGeolocation?.edges?.filter((edge: any) => !!edge?.node?.name);
        else if (locationQueryType === searchRestaurantsByLocation)
          validData = data?.searchRestaurantsByLocation?.edges?.filter((edge: any) => !!edge?.node?.name);
        if (locationQueryType === searchRestaurantsByOpenText)
          validData = data?.searchRestaurantsByOpenText?.edges?.filter((edge: any) => !!edge?.node?.name);

        if (validData?.length === 0) {
          insufficentDataError = true;
          throw new Error('Insufficent data error');
        } else {
          insufficentDataError = false;

          if (locationQueryType === searchRestaurantsByGeolocation)
            searchResults = {
              ...data,
              searchRestaurantsByGeolocation: { ...data?.searchRestaurantsByGeolocation, edges: validData },
            };
          else if (locationQueryType === searchRestaurantsByLocation)
            searchResults = {
              ...data,
              searchRestaurantsByLocation: { ...data?.searchRestaurantsByLocation, edges: validData },
            };
          if (locationQueryType === searchRestaurantsByOpenText)
            searchResults = {
              ...data,
              searchRestaurantsByOpenText: { ...data?.searchRestaurantsByOpenText, edges: validData },
            };
        }
      } else throw new Error('Insufficent data error');
    } catch (error) {
      if (searchResults) searchResultsError = false;
      else searchResultsError = true;
      const errorCode = Number(getHttpStatusCode(error));
      const errorMessage = getErrorMessage(errorCode);
      if (errorCode) setErrorState(errorCode, errorMessage);
      else if (window.location.search.includes(AWPFILTERS.distance)) setErrorState(0, 'mileGenericError');
      else if (insufficentDataError) setErrorState(0, 'openTextError');
      else setErrorState(0, 'mileError');
      setData([], false, true, []);
      setIsLoaderVisible(false);
      enableListView();
    } finally {
      if (
        !searchResultsError &&
        (searchResults?.searchRestaurantsByGeolocation?.edges?.length >= 0 ||
          searchResults?.searchRestaurantsByLocation?.edges?.length >= 0 ||
          searchResults?.searchRestaurantsByOpenText?.edges?.length >= 0)
      ) {
        if (
          searchResults?.searchRestaurantsByGeolocation?.edges?.length > 0 ||
          searchResults?.searchRestaurantsByLocation?.edges?.length > 0 ||
          searchResults?.searchRestaurantsByOpenText?.edges?.length > 0
        ) {
          filterUpdateData = searchResults?.searchRestaurantsByGeolocation
            ? searchResults?.searchRestaurantsByGeolocation
            : searchResults?.searchRestaurantsByOpenText
            ? searchResults?.searchRestaurantsByOpenText
            : searchResults?.searchRestaurantsByLocation;
          if (
            searchResults?.searchRestaurantsByGeolocation?.status[0]['__typename'] ===
            'RestaurantsWithExpandedSearchRadiusInfo'
          ) {
            setErrorState(0, props?.expandResultsError);
          } else setErrorState(0, '');
          setData(searchResults, false, false, props?.dynamicMedia);
          if (
            searchResults?.searchRestaurantsByOpenText &&
            searchResults?.searchRestaurantsByOpenText?.edges?.length > 1 &&
            searchResults?.searchRestaurantsByOpenText?.status?.[0]?.['code'] === 'SPELL_CORRECTED_INFO'
          ) {
            replacePlaceholderText(
              searchResults?.searchRestaurantsByOpenText?.status?.[0]?.requestedSearchTerm,
              searchResults?.searchRestaurantsByOpenText?.status?.[0]?.correctedSearchTerm
            );
          } else {
            setSearchTypeMessage(sortMessage, searchTextMessage, searchTextMessage);
          }
          if (filterUpdateData?.facets?.length > 0) {
            const query = new URLSearchParams(queryParam);

            const categorizedBrands = getCategorizedBrands(filterUpdateData?.facets, props?.categorizedBrandsArray);
            const values: any = query.get('brandId') ? query.get('brandId') : '';
            let selectedVal: string | any[] = [];
            selectedVal = values.split(',');
            const selectedBrandArr: any[] = [];
            categorizedBrands?.forEach((item: { brands: any[] }) => {
              item?.brands?.forEach((element: { brandTagId: any }) => {
                if (selectedVal.includes(element.brandTagId)) selectedBrandArr.push(element);
              });
            });
            brandSelection(selectedBrandArr);
            updateStoreData('hour', 'hours', hourSelection, query);
            updateStoreData('cuisine', 'cuisine', cuisineSelection, query);
            updateStoreData('seating', 'seating', seatingSelection, query);
            updateStoreData('bonvoyMember', 'bonvoyMembers', bonvoySelection, query);
            updateStoreData('city', 'city', citySelection, query);
            updateStoreData('stateProvinceDescription', 'stateProvinceDescription', stateProvinceDescSelection, query);
            updateStoreData('countryDescription', 'countryDescription', countryDescSelection, query);
            updateStoreData('venueType', 'venueType', venueTypeSelection, query);
            updateStoreData('dietaryOptions', 'dietaryOptions', dietaryOptionsSelection, query);
            updateStoreData('experiences', 'experiences', experiencesSelection, query);
            updateStoreData('price', 'price', priceSelection, query);
          }
        } else {
          if (isOpenTextSearch) {
            setErrorState(0, 'openTextError');
            setData([], false, false, []);
          } else {
            if (window.location.search.includes(AWPFILTERS.distance)) setErrorState(0, 'mileGenericError');
            else setErrorState(0, 'mileError');
            setData([], false, false, []);
          }
        }
        setIsLoaderVisible(false);
      }
    }
  }

  const cardContainer = (index: number) => {
    return (
      <ResponsiveGrid
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        pagePath={pagePath}
        itemPath={`${itemPath}/${totalNumberOfCards[index]}`}
        columnCount="12"
        gridClassNames={''}
        config={{
          isEmpty: () => true,
          resourceType: `mi-aem-common-spa/components/container`,
        }}
      />
    );
  };

  const getListCta = (className?: string) => {
    return (
      <div className={clsx('text-center cta-container custom_click_track', className)}>
        <Button
          isLink={false}
          className={clsx('m-button-gallery map-button')}
          aria-label={ctaLabel}
          buttonCopy={ctaLabel}
          isTextBeforeChildren={false}
          callback={ctaLabel === props?.listLabel ? enableListView : searchThisArea}
          custom_click_track_value={`${SEARCH_RESULT_PAGE}|${ctaLabel} CTA|${INTERNAL_LINK}`}
        >
          <Icon
            iconClass={ctaLabel === props?.listLabel ? props?.listIconPath : props?.searchThisAreaIcon}
            ariaLabel={ctaLabel === props?.listLabel ? props?.listIconPath : props?.searchThisAreaIcon}
          />
        </Button>
      </div>
    );
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const cardComponent = (cardName: string, jsonData: any) => {
    // eslint-disable-next-line no-prototype-builtins
    if (jsonData?.hasOwnProperty(cardName)) {
      const card = jsonData[cardName];
      const cardItems = card[':items'];
      for (const itemKey in cardItems) {
        // eslint-disable-next-line no-prototype-builtins
        if (cardItems?.hasOwnProperty(itemKey)) {
          const item = cardItems[itemKey];
          return <PromotionBanner {...item} isMapView={isMapView} />;
        }
      }
    }
    return null;
  };

  const callSegmentApi = async () => {
    const sessionDataObject = window?.['sessionData']?.cacheData?.data;
    const segmentPayload = targetPayloadRequest(
      {
        isUxl: 'false',
        mboxName:
          process.env['TARGET_SEGMENT_MBOX_NAME'] ||
          useClientEnvVarsStore.getState().envVarsObject['TARGET_SEGMENT_MBOX_NAME'],
        propertyToken:
          process.env['TARGET_SEGMENT_PROPERTY_TOKEN'] ||
          useClientEnvVarsStore.getState().envVarsObject['TARGET_SEGMENT_PROPERTY_TOKEN'],
      },
      {
        env_site_id: window?.['dataLayer']?.['env_site_id'],
        mr_prof_authentication_state: window?.['dataLayer']?.['mr_prof_authentication_state'],
        page_requested_uri: window?.['dataLayer']?.['page_requested_uri'],
        page_url_query_string: window?.['dataLayer']?.['page_url_query_string'],
        referrer_path: window?.['dataLayer']?.['referrer_path'],
        search_currency_type: window?.['dataLayer']?.['search_currency_type'],
      }
    );
    segmentPayload.id.tntId = getAWPTntId(window?.['dataLayer']);
    const consumerId = sessionDataObject?.consumerID;
    if (consumerId) {
      segmentPayload.id.thirdPartyId = consumerId;
    }
    try {
      const response = await Axios.post(TARGET_SEGMENT_API_ENDPOINT, {
        targetReqPayload: segmentPayload,
      });
      const segmentId = response?.data?.segmentId;

      if (segmentId && segmentId.trim() !== '') {
        setSegmentIdValue(segmentId);
        if (segmentId === targetMapControl) {
          sessionStorage.setItem('awpMapViewEnabled', 'true');
          setIsMapView(true);
          setIsMapViewEnabled(true);
        } else {
          sessionStorage.setItem('awpMapViewEnabled', 'false');
          sessionStorage.setItem('awpMapFullScreen', 'false');
          setIsMapView(false);
          setIsMapViewEnabled(false);
          setIsMapOnFullScreen(false);
        }
        setIsTargetCallCompleted(true);
      } else setIsTargetCallCompleted(true);
    } catch (error) {
      sessionStorage.setItem('awpMapViewEnabled', 'false');
      sessionStorage.setItem('awpMapFullScreen', 'false');
      setIsMapView(false);
      setIsMapViewEnabled(false);
      setIsMapOnFullScreen(false);
      setIsTargetCallCompleted(true);
    }
  };

  // useEffect(() => {
  //   // Add the segment class once the container is mounted and we have a segment id
  //   if (!segmentIdValue || !isTargetCallCompleted) return;
  //   const searchContainerDiv = document.getElementById('search-results-container-div');
  //   if (searchContainerDiv && !searchContainerDiv.classList.contains(segmentIdValue)) {
  //     searchContainerDiv.classList.add(segmentIdValue);
  //   }
  // }, [segmentIdValue, isTargetCallCompleted]);

  if (searchResultsDataError || isComponentLoading) {
    return null;
  }
  return (
    <div id="search-results-container-div" className={segmentIdValue}>
      {searchResultDataLoading && !errorMessage ? (
        <div className="pt-5 pb-5">
          <SkeletonOutletCardLoader isMapView={isMapView} windowHeight={windowHeight} offsetHeight={offsetHeight} />
        </div>
      ) : !errorCheck && searchResultsData && searchResultsData?.total > 0 && isTargetCallCompleted ? (
        <StyledSearchResultContainer
          data-component-name="o-awp-searchresults"
          data-testid="searchresults"
          className={clsx(isMapView ? 'map-view-container' : 'list-view-container', props?.styleclass)}
          windowHeight={windowHeight}
          offsetHeight={offsetHeight}
        >
          <GlobalStyle />
          {isMapView && <GlobalUnsetMinHeight />}
          {isMapView && isIOSDevice && <GlobalScrollIOS />}
          {/* <div className={clsx(isMobileViewPort ? ' ' : 'p-0')}> */}
          <div
            className={clsx('container', {
              'px-md-0': props?.styleclass?.includes(fullbleedClass),
              'p-0': !props?.styleclass?.includes(fullbleedClass),
            })}
          >
            <SearchAllFilters
              {...props}
              isMobileViewPort={isMobileViewPort}
              currentPage={currentPageFromContext}
              isMapView={isMapView}
            />
          </div>
          <div
            className={clsx(
              isMapView
                ? 'd-flex map-view-container__parent m-container-fullbleed'
                : 'list-view-container__parent container',
              {
                'mb-3': !(!isMapView && !searchResultDataLoading) && searchResultsData?.total < PageSize,
              }
            )}
          >
            <div
              className={clsx(
                isMapView ? 'map-view-container__search-outlets' : '',
                isMapOnFullScreen && isMapView ? 'd-none' : ''
              )}
            >
              {searchTextMessageDisplay &&
                (!searchTextMessageDisplay.includes('[') || !searchTextMessageDisplay.includes(']')) && (
                  <p
                    className={clsx(
                      isMapView ? 't-font-s px-3' : isMobileViewPort ? 'pt-3 pb-0 t-subtitle-m' : 'pt-5 t-subtitle-l',
                      'm-0'
                    )}
                  >
                    {searchTextMessageDisplay}
                  </p>
                )}
              <div
                className={clsx(
                  isMapView ? 'px-3' : '',
                  isMobileViewPort ? 'pt-4' : 'pt-3',
                  'd-flex justify-content-between'
                )}
              >
                <p
                  className={clsx(
                    isMapView ? 't-font-s' : isMobileViewPort ? 't-font-s' : 't-subtitle-l',
                    'pb-1',
                    'mb-0 pagination-details'
                  )}
                >
                  {props?.restaurantCountLabel
                    ?.replace('[result]', `${startIndex + 1}-${endIndex}`)
                    ?.replace('[total]', `${searchResultsData?.total ? searchResultsData?.total : '0'}`)}
                </p>
                {sortMessage && (
                  <p className={clsx(isMapView ? 't-font-s' : isMobileViewPort ? 't-font-s' : 't-subtitle-l', 'm-0')}>
                    {sortMessage}
                  </p>
                )}
              </div>

              {errorMessage && isMapView && (
                <StyledErrorMessage
                  data-component-name="m-awp-AWPErrorMessage"
                  data-testid="awp-AWPErrorMessage"
                  isEmpty={!errorMessage}
                  className={clsx(
                    'container awp-error-container p-0 mx-3 awp-additional-container',
                    errorMessage ? 'error-message-active' : ''
                  )}
                >
                  <div
                    className={clsx(
                      'm-message-inline col-12 awp-error-message mt-3 mb-3',
                      props[errorMessage as keyof typeof props] ? 'error-sev2' : 'info'
                    )}
                  >
                    <div className="m-message-content-wrap">
                      <div className="m-message-content">
                        <span>
                          {awperror[errorMessage as keyof typeof props]
                            ? awperror[errorMessage as keyof typeof props]
                            : errorMessage}
                        </span>
                      </div>
                    </div>
                  </div>
                </StyledErrorMessage>
              )}
              <div className={clsx(isMapView ? 'px-3' : '')}>
                {searchResultsData?.edges?.map((res: any, index: number) =>
                  index === promBannerIndex && currentPage === outletPromotionCurrPage ? (
                    <div key={`search-outlet-card-${index}`}>
                      {isAuthorMode &&
                        cardCount &&
                        Array.from({ length: totalNumberOfCards.length }, (_, i) => cardContainer(i))}
                      {!isAuthorMode &&
                        cardCount &&
                        totalNumberOfCards.map((cardName: string) => cardComponent(cardName, cqItems))}
                      <SearchOutletCard
                        restaurant={res}
                        {...props}
                        campaignParams={campaignParams}
                        currentLocale={subLocale}
                        isMapView={isMapView}
                        index={index}
                      />
                    </div>
                  ) : (
                    <SearchOutletCard
                      key={`search-outlet-card-${index}`}
                      restaurant={res}
                      {...props}
                      campaignParams={campaignParams}
                      currentLocale={subLocale}
                      isMapView={isMapView}
                      index={index}
                    />
                  )
                )}
              </div>

              {!searchResultDataLoading && searchResultsData?.total > PageSize && (
                <div
                  className={clsx('pagination-container', isMapView ? 'py-3 mt-0' : 'mt-5')}
                  data-testid="pagination-panel"
                >
                  <PaginationPanel
                    currentPage={currentPage}
                    totalCount={searchResultsData?.total}
                    pageSize={PageSize}
                    onPageChange={(page: number): void => onPageItemClick(page)}
                    clickTrackLocation={TRACKING_CONSTANT.SEARCH_RESULTS_PAGINATION}
                    labels={{
                      previous: props?.previousButtonLabel,
                      next: props?.nextButtonLabel,
                    }}
                  />
                </div>
              )}
            </div>

            {isMapView && (
              <>
                <div
                  className={clsx(
                    'map-view-container__map',
                    'custom_click_track',
                    !isMapView ? 'map-view-container__map-hide' : ''
                  )}
                >
                  <Button
                    isLink={false}
                    className={clsx('m-button-gallery toggle-sidebar', isMapView ? 'toggle-sidebar-hide' : '')}
                    aria-label={
                      isMapOnFullScreen
                        ? isDirectionRTL
                          ? props?.collapseIconPath
                          : props?.expandIconPath
                        : isDirectionRTL
                        ? props?.expandIconPath
                        : props?.collapseIconPath
                    }
                    callback={toggleSideSearchResults}
                  >
                    <Icon
                      iconClass={
                        isMapOnFullScreen
                          ? isDirectionRTL
                            ? props?.collapseIconPath
                            : props?.expandIconPath
                          : isDirectionRTL
                          ? props?.expandIconPath
                          : props?.collapseIconPath
                      }
                      ariaLabel={
                        isMapOnFullScreen
                          ? isDirectionRTL
                            ? props?.collapseIconPath
                            : props?.expandIconPath
                          : isDirectionRTL
                          ? props?.expandIconPath
                          : props?.collapseIconPath
                      }
                    />
                  </Button>
                  {!searchResultDataLoading && searchResultsData?.total && (
                    <GoogleMapComponent {...props} currentLocale={subLocale} awperror={awperror} />
                  )}
                </div>

                {getListCta('list-cta-mobile')}

                <div className="d-flex flex-row search-cta-container">
                  {getListCta('list-cta-desktop')}

                  <div className={clsx('text-center current-location-cta custom_click_track')}>
                    <Button
                      isLink={false}
                      className={clsx('m-button-gallery current-location-button')}
                      aria-label={props?.currentLocationMapLabel}
                      buttonCopy={isMobileViewPort ? '' : props?.currentLocationMapLabel}
                      isTextBeforeChildren={false}
                      callback={enableCurrentLocationSearch}
                      id="current-location-button"
                      custom_click_track_value={`${SEARCH_RESULT_PAGE}|${MAP_CURRENT_LOCATION}|${INTERNAL_LINK}`}
                    >
                      <Icon iconClass={props?.currentLocationIconPath} ariaLabel={props?.currentLocationIconPath} />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
          {!isMapView && !searchResultDataLoading && props?.mapLabel && (
            <div
              className={clsx(
                'text-center cta-container map-cta pb-3 custom_click_track',
                !searchResultDataLoading && searchResultsData?.total < PageSize ? 'mt-3' : ''
              )}
            >
              <Button
                isLink={false}
                className={clsx('m-button-gallery map-button')}
                aria-label={props?.mapLabel}
                buttonCopy={props?.mapLabel}
                isTextBeforeChildren={false}
                callback={enableMapView}
                custom_click_track_value={`${SEARCH_RESULT_PAGE}|${MAP_VIEW}|${INTERNAL_LINK}`}
              >
                <Icon iconClass={props?.mapIconPath} ariaLabel={props?.mapIconPath} />
              </Button>
            </div>
          )}
          {/* </div> */}
          {/* </StyledSearchResults> */}
        </StyledSearchResultContainer>
      ) : null}
    </div>
  );
};

export const SearchResultsConfig = {
  emptyLabel: 'SearchResults',
  isEmpty: false,
  resourceType: `${NEXT_PUBLIC_AEM_SITE}/components/content/searchresults`,
};

export const SearchResultsEditable = (props: any) => (
  <EditableComponent config={SearchResultsConfig} {...props}>
    <SearchResults {...props} />
  </EditableComponent>
);
